import { type RovoContentBridgeAPI } from './rovo-bridge-api';
import { findTopmostSameOriginWindow } from './transport/transports/browser-apis';

/* eslint-disable no-console */

declare global {
	interface Window {
		/**
		 * @description Used internally by @atlassian/rovo-content-bridge-api. DO NOT USE.
		 * @deprecated
		 */
		__rovoContentBridgeAPI__: RovoContentBridgeAPI | undefined;

		/**
		 * @description Used internally by @atlassian/rovo-content-bridge-api. DO NOT USE.
		 * @deprecated
		 */
		__rovoBridgeDebuggerStart__: VoidFunction | undefined;
	}
}

/**
 * Attaches a given bridge API instance to the topmost same-origin window.
 *
 * @description In same-origin contexts, the bridge API should be accessed from the topmost same-origin window
 * since the JavaScript context is shared. We find the topmost accessible window in the parent chain and mount
 * the bridge API there, enabling nested same-origin iframes to share the same bridge instance.
 *
 * @param bridgeAPI instance to attach
 */
export const mountAPI = (bridgeAPI: RovoContentBridgeAPI): void => {
	try {
		const topmostSameOriginWindow = findTopmostSameOriginWindow(window);

		// do not override if already existing on topmost same-origin window
		if (topmostSameOriginWindow.__rovoContentBridgeAPI__) {
			return;
		}

		topmostSameOriginWindow.__rovoContentBridgeAPI__ = bridgeAPI;
		topmostSameOriginWindow.__rovoBridgeDebuggerStart__ = startDebugger;
	} catch {
		// bail out if any errors take place (browser quirks on accessing window.top from a cross-origin frame)
		return;
	}
};

/**
 * Reads a given bridge API instance off the topmost same-origin window.
 * @returns null if bridge API is not present; otherwise yields the bridge API instance
 * from the topmost same-origin window.
 */
export const readAPI = () => {
	try {
		const topmostSameOriginWindow = findTopmostSameOriginWindow(window);
		return topmostSameOriginWindow.__rovoContentBridgeAPI__ ?? null;
	} catch {
		return null;
	}
};

/**
 * Clears shared reference to bridge API for GC purposes.
 */
export const clearAPI = (): void => {
	try {
		const topmostSameOriginWindow = findTopmostSameOriginWindow(window);
		topmostSameOriginWindow.__rovoContentBridgeAPI__ = undefined as any;
		// Also clear debug API to prevent memory leaks
		topmostSameOriginWindow.__rovoBridgeDebuggerStart__ = undefined as any;
	} catch {
		return;
	}
};

async function startDebugger() {
	try {
		await import('./debug/bridge-api-debugger-api');
	} catch (err) {
		console.error('failed to initialise rovo-debug API', err);
	}
}
